import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/system_settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastError } from "@/lib/api-errors";
import type { SystemSettingsView } from "@/types/api";

/** Сервис Честного знака: адрес, учётка и обязательность relay.
 *
 * ⚠️ Пароль отправляется, ТОЛЬКО если его ввели: пустое поле означает «не
 * менять», а не «стереть» — иначе сохранение адреса стирало бы учётку. */
export function HonestSignCard({
  data,
}: {
  data: SystemSettingsView;
}): React.ReactElement {
  const qc = useQueryClient();
  const [baseUrl, setBaseUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [relayRequired, setRelayRequired] = useState(true);

  useEffect(() => {
    setBaseUrl(data.honest_sign_base_url ?? "");
    setUsername(data.honest_sign_username ?? "");
    setRelayRequired(data.honest_sign_relay_required);
  }, [data]);

  const updateMut = useMutation({
    mutationFn: api.updateSystemSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system-settings"] });
      setPassword("");
      toast.success("Сохранено");
    },
    onError: toastError("Ошибка сохранения"),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: api.SystemSettingsUpdate = {
      honest_sign_base_url: baseUrl || null,
      honest_sign_username: username || null,
      honest_sign_relay_required: relayRequired,
    };
    if (password) body.honest_sign_password = password;
    updateMut.mutate(body);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Сервис Честного знака</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="hs_url">Адрес сервиса</Label>
            <Input
              id="hs_url"
              placeholder="http://192.168.95.19:8833"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="hs_user">Логин</Label>
              <Input
                id="hs_user"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hs_pwd">
                Пароль{" "}
                {data.has_honest_sign_password && (
                  <span className="text-xs">(оставьте пустым чтобы не менять)</span>
                )}
              </Label>
              <Input
                id="hs_pwd"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={relayRequired}
              onChange={(e) => setRelayRequired(e.target.checked)}
              className="size-4"
            />
            Обязательный relay (отключить прямые запросы с устройств — всегда через
            backend ↔ hs-relay-agent)
          </label>
          <Button type="submit" disabled={updateMut.isPending}>
            Сохранить
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
